import { useDashboardData } from "../../../context/DashboardContext";
import { MetricCard } from "../../MetricCard";
import type { WidgetComponentProps } from "../types";

// Icon components (simple SVGs)
const icons: Record<string, React.ReactNode> = {
  dollar: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  trending: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
  target: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
  chart: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
  users: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  wallet: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>,
  percent: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
};

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatCurrencyAbbreviated = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return formatCurrency(value);
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const formatNumber = (value: number) => value.toLocaleString();

type FormatType = "currency" | "currencyAbbreviated" | "percent" | "number";
type MetricColor = "brand" | "green" | "blue" | "purple" | "red";
type SparklineDatum = number[] | Array<{ value?: number | null }>;

const formatters: Record<FormatType, (v: number) => string> = {
  currency: formatCurrency,
  currencyAbbreviated: formatCurrencyAbbreviated,
  percent: formatPercent,
  number: formatNumber,
};

export default function MetricWidget({ config, title }: WidgetComponentProps) {
  const { metrics, chartData } = useDashboardData();

  const metricKey = config.metricKey as string;
  const format = (config.format as FormatType) || "currency";
  const iconKey = (config.icon as string) || "dollar";
  const color = (config.color as MetricColor) || "brand";
  const sparklineKey = config.sparklineKey as string | undefined;

  const value = metrics[metricKey] ?? 0;
  const formatter = formatters[format] || formatCurrency;
  const rawSparkline = sparklineKey ? (chartData[sparklineKey] as SparklineDatum | undefined) : undefined;
  const sparklineData = Array.isArray(rawSparkline)
    ? rawSparkline.map((point) => {
        if (typeof point === "number") return point;
        if (point && typeof point === "object" && typeof point.value === "number") return point.value;
        return 0;
      })
    : undefined;

  return (
    <MetricCard
      label={title}
      value={formatter(value)}
      icon={icons[iconKey]}
      color={color}
      data={sparklineData || []}
      tooltipFormatter={format === "percent" ? formatPercent : formatCurrency}
      index={0}
    />
  );
}
